'use client';

import { c as _c } from "react/compiler-runtime";
import type { SortDirection } from '@tanstack/react-table';
import * as React from 'react';
import { cn } from '../../utils/cn';
import { FLUID_COLUMN } from '../../utils/grids';
import { Skeleton } from './Skeleton';
type TableProps = React.ComponentProps<'div'> & {
  /**
   * Optional CSS grid-template-columns value (e.g. 'auto 1fr auto').
   * Applied to all rows via a CSS variable so header and body align.
   */
  gridTemplateColumns?: string;
  /**
   * When provided, and `columns` is not, the grid template will be computed as
   * `repeat(columnCount, minmax(0, 1fr))` so that all columns are even.
   */
  columnCount?: number;
  /**
   * Minimum table width in pixels. When set, triggers horizontal scroll
   * if the container is narrower than this value.
   */
  minWidth?: number;
  /**
   * `visible` drops the built-in `overflow-x-auto` container. Any overflow value other than
   * `visible` makes the container a scroll container, which traps `position: sticky` descendants
   * inside it — pass `visible` when they must stick to an ancestor scroller instead; the caller
   * then owns horizontal overflow.
   */
  overflowX?: 'auto' | 'visible';
};
type TableStyle = React.CSSProperties & {
  ['--table-columns']?: string;
};
const Table = React.forwardRef<HTMLDivElement, TableProps>(function Table(t0, ref) {
  const $ = _c(38);
  let className;
  let columnCount;
  let gridTemplateColumns;
  let minWidth;
  let props;
  let style;
  let t1;
  if ($[0] !== t0) {
    ({
      className,
      style,
      gridTemplateColumns,
      columnCount,
      minWidth,
      overflowX: t1,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = columnCount;
    $[3] = gridTemplateColumns;
    $[4] = minWidth;
    $[5] = props;
    $[6] = style;
    $[7] = t1;
  } else {
    className = $[1];
    columnCount = $[2];
    gridTemplateColumns = $[3];
    minWidth = $[4];
    props = $[5];
    style = $[6];
    t1 = $[7];
  }
  const overflowX = t1 === undefined ? "auto" : t1;
  const gridTemplateColumnsFallback = columnCount && columnCount > 0 ? `repeat(${columnCount}, ${FLUID_COLUMN})` : "1fr";
  const t2 = style as React.CSSProperties;
  const t3 = gridTemplateColumns ?? gridTemplateColumnsFallback;
  let t4;
  if ($[8] !== t2 || $[9] !== t3) {
    t4 = {
      ...t2,
      "--table-columns": t3
    };
    $[8] = t2;
    $[9] = t3;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  const styleWithColumns = t4;
  if (minWidth) {
    const t5 = overflowX === "auto" && "overflow-x-auto";
    let t6;
    if ($[11] !== t5) {
      t6 = cn("relative min-h-0 w-full rounded-lg border", t5);
      $[11] = t5;
      $[12] = t6;
    } else {
      t6 = $[12];
    }
    let t7;
    if ($[13] !== className) {
      t7 = cn("flex min-h-0 w-full flex-col text-sm", className);
      $[13] = className;
      $[14] = t7;
    } else {
      t7 = $[14];
    }
    let t8;
    if ($[15] !== minWidth || $[16] !== styleWithColumns) {
      t8 = {
        ...styleWithColumns,
        minWidth
      };
      $[15] = minWidth;
      $[16] = styleWithColumns;
      $[17] = t8;
    } else {
      t8 = $[17];
    }
    let t9;
    if ($[18] !== props || $[19] !== ref || $[20] !== t7 || $[21] !== t8) {
      t9 = <div data-slot="table" role="table" className={t7} style={t8} ref={ref} {...props} />;
      $[18] = props;
      $[19] = ref;
      $[20] = t7;
      $[21] = t8;
      $[22] = t9;
    } else {
      t9 = $[22];
    }
    let t10;
    if ($[23] !== t6 || $[24] !== t9) {
      t10 = <div data-slot="table-container" className={t6}>{t9}</div>;
      $[23] = t6;
      $[24] = t9;
      $[25] = t10;
    } else {
      t10 = $[25];
    }
    return t10;
  }
  const t5 = overflowX === "auto" && "h-full overflow-x-auto";
  let t6;
  if ($[26] !== t5) {
    t6 = cn("relative min-h-0 w-full", t5);
    $[26] = t5;
    $[27] = t6;
  } else {
    t6 = $[27];
  }
  let t7;
  if ($[28] !== className) {
    t7 = cn("flex min-h-0 w-full caption-bottom flex-col rounded-lg border text-sm", className);
    $[28] = className;
    $[29] = t7;
  } else {
    t7 = $[29];
  }
  let t8;
  if ($[30] !== props || $[31] !== ref || $[32] !== styleWithColumns || $[33] !== t7) {
    t8 = <div data-slot="table" role="table" className={t7} style={styleWithColumns} ref={ref} {...props} />;
    $[30] = props;
    $[31] = ref;
    $[32] = styleWithColumns;
    $[33] = t7;
    $[34] = t8;
  } else {
    t8 = $[34];
  }
  let t9;
  if ($[35] !== t6 || $[36] !== t8) {
    t9 = <div data-slot="table-container" className={t6}>{t8}</div>;
    $[35] = t6;
    $[36] = t8;
    $[37] = t9;
  } else {
    t9 = $[37];
  }
  return t9;
});
function TableHeader(t0) {
  const $ = _c(14);
  let className;
  let props;
  let sticky;
  let style;
  if ($[0] !== t0) {
    ({
      className,
      sticky,
      style,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
    $[3] = sticky;
    $[4] = style;
  } else {
    className = $[1];
    props = $[2];
    sticky = $[3];
    style = $[4];
  }
  const t1 = sticky && "sticky top-0 z-20";
  let t2;
  if ($[5] !== className || $[6] !== t1) {
    t2 = cn("bg-background rounded-t-lg border-b-(length:--default-border-width)", t1, className);
    $[5] = className;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  const t3 = style as React.CSSProperties;
  let t4;
  if ($[8] !== t3) {
    t4 = {
      ...t3,
      borderRightWidth: "var(--scrollbar-width, 0px)",
      borderRightColor: "transparent"
    };
    $[8] = t3;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== props || $[11] !== t2 || $[12] !== t4) {
    t5 = <div data-slot="table-header" role="rowgroup" className={t2} style={t4} {...props} />;
    $[10] = props;
    $[11] = t2;
    $[12] = t4;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  return t5;
}
function TableBody(t0) {
  const $ = _c(20);
  let className;
  let maxHeight;
  let props;
  let scrollable;
  let style;
  if ($[0] !== t0) {
    ({
      className,
      style,
      scrollable,
      maxHeight,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = maxHeight;
    $[3] = props;
    $[4] = scrollable;
    $[5] = style;
  } else {
    className = $[1];
    maxHeight = $[2];
    props = $[3];
    scrollable = $[4];
    style = $[5];
  }
  const bodyRef = React.useRef(null);
  let t1;
  let t2;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => {
      const updateScrollbarVar = function updateScrollbarVar() {
        const current = bodyRef.current;
        if (!current) {
          return;
        }
        const scrollbarWidth = current.offsetWidth - current.clientWidth;
        current.parentElement?.style.setProperty("--scrollbar-width", `${Math.max(0, scrollbarWidth)}px`);
      };
      updateScrollbarVar();
      const current_0 = bodyRef.current;
      if (!current_0) {
        return;
      }
      const resizeObserver = new ResizeObserver(() => updateScrollbarVar());
      resizeObserver.observe(current_0);
      return () => resizeObserver.disconnect();
    };
    t2 = [];
    $[6] = t1;
    $[7] = t2;
  } else {
    t1 = $[6];
    t2 = $[7];
  }
  React.useLayoutEffect(t1, t2);
  const t3 = scrollable && "overflow-y-auto";
  let t4;
  if ($[8] !== className || $[9] !== t3) {
    t4 = cn("[&>[data-slot=table-row]:last-child]:border-0", t3, className);
    $[8] = className;
    $[9] = t3;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  let t5;
  if ($[11] !== style) {
    t5 = style as React.CSSProperties || {};
    $[11] = style;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  const t6 = scrollable ? maxHeight : undefined;
  let t7;
  if ($[13] !== t5 || $[14] !== t6) {
    t7 = {
      ...t5,
      maxHeight: t6
    };
    $[13] = t5;
    $[14] = t6;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  let t8;
  if ($[16] !== props || $[17] !== t4 || $[18] !== t7) {
    t8 = <div data-slot="table-body" role="rowgroup" className={t4} ref={bodyRef} style={t7} {...props} />;
    $[16] = props;
    $[17] = t4;
    $[18] = t7;
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  return t8;
}
function TableFooter(t0) {
  const $ = _c(8);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn("bg-muted/50 border-t-[0.5px] font-medium [&>[data-slot=table-row]:last-child]:border-0", className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== t1) {
    t2 = <div data-slot="table-footer" role="rowgroup" className={t1} {...props} />;
    $[5] = props;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
}
function TableRow(t0) {
  const $ = _c(27);
  let className;
  let isLoading;
  let onClick;
  let props;
  let style;
  let t1;
  if ($[0] !== t0) {
    ({
      className,
      style,
      isLoading,
      enableHover: t1,
      onClick,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = isLoading;
    $[3] = onClick;
    $[4] = props;
    $[5] = style;
    $[6] = t1;
  } else {
    className = $[1];
    isLoading = $[2];
    onClick = $[3];
    props = $[4];
    style = $[5];
    t1 = $[6];
  }
  const enableHover = t1 === undefined ? false : t1;
  let t2;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      gridTemplateColumns: "var(--table-columns, 1fr)",
      gridAutoColumns: "1fr",
      gridAutoFlow: "column"
    };
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  const rowStyle = t2;
  if (isLoading) {
    let t3;
    if ($[8] !== className) {
      t3 = cn("h-10 w-full", className);
      $[8] = className;
      $[9] = t3;
    } else {
      t3 = $[9];
    }
    let t4;
    if ($[10] !== style) {
      t4 = {
        ...rowStyle,
        ...style
      };
      $[10] = style;
      $[11] = t4;
    } else {
      t4 = $[11];
    }
    let t5;
    if ($[12] !== props || $[13] !== t3 || $[14] !== t4) {
      t5 = <Skeleton role="row" data-slot="table-row" className={t3} style={t4} {...props} />;
      $[12] = props;
      $[13] = t3;
      $[14] = t4;
      $[15] = t5;
    } else {
      t5 = $[15];
    }
    return t5;
  }
  const t3 = enableHover && "hover:bg-muted/50";
  const t4 = onClick && "cursor-pointer";
  let t5;
  if ($[16] !== className || $[17] !== t3 || $[18] !== t4) {
    t5 = cn("data-[state=selected]:bg-muted group grid transition-colors", t3, t4, className);
    $[16] = className;
    $[17] = t3;
    $[18] = t4;
    $[19] = t5;
  } else {
    t5 = $[19];
  }
  let t6;
  if ($[20] !== style) {
    t6 = {
      ...rowStyle,
      ...style
    };
    $[20] = style;
    $[21] = t6;
  } else {
    t6 = $[21];
  }
  let t7;
  if ($[22] !== onClick || $[23] !== props || $[24] !== t5 || $[25] !== t6) {
    t7 = <div data-slot="table-row" role="row" className={t5} style={t6} onClick={onClick} {...props} />;
    $[22] = onClick;
    $[23] = props;
    $[24] = t5;
    $[25] = t6;
    $[26] = t7;
  } else {
    t7 = $[26];
  }
  return t7;
}
function TableHead(t0) {
  const $ = _c(10);
  let className;
  let props;
  let sticky;
  if ($[0] !== t0) {
    ({
      className,
      sticky,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
    $[3] = sticky;
  } else {
    className = $[1];
    props = $[2];
    sticky = $[3];
  }
  let t1;
  if ($[4] !== className || $[5] !== sticky) {
    t1 = cn("text-secondary-foreground hover:text-foreground group/head relative flex h-10 items-center px-4 text-left align-middle text-xs whitespace-nowrap transition-colors [&:has([role=checkbox])]:pr-0 [&>[role=checkbox]]:translate-y-[2px]", getStickyCellClassName(sticky, true), className);
    $[4] = className;
    $[5] = sticky;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  let t2;
  if ($[7] !== props || $[8] !== t1) {
    t2 = <div data-slot="table-head" role="columnheader" className={t1} {...props} />;
    $[7] = props;
    $[8] = t1;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  return t2;
}
function TableCell(t0) {
  const $ = _c(10);
  let className;
  let props;
  let sticky;
  if ($[0] !== t0) {
    ({
      className,
      sticky,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
    $[3] = sticky;
  } else {
    className = $[1];
    props = $[2];
    sticky = $[3];
  }
  let t1;
  if ($[4] !== className || $[5] !== sticky) {
    t1 = cn("relative px-4 py-3 align-middle whitespace-nowrap [&:has([role=checkbox])]:pr-0 [&>[role=checkbox]]:translate-y-[2px]", getStickyCellClassName(sticky), className);
    $[4] = className;
    $[5] = sticky;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  let t2;
  if ($[7] !== props || $[8] !== t1) {
    t2 = <div data-slot="table-cell" role="cell" className={t1} {...props} />;
    $[7] = props;
    $[8] = t1;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  return t2;
}
function getStickyCellClassName(sticky?: 'left' | 'right', isHeader?: boolean) {
  if (!sticky) return undefined;
  return cn('sticky from-transparent z-10 via-card to-card', 'after:pointer-events-none group-hover:after:absolute group-hover:after:inset-0 group-hover:after:bg-card', {
    'z-30': isHeader,
    'left-0 not-group-hover:bg-gradient-to-l group-hover:bg-card': sticky === 'left',
    'right-0 not-group-hover:bg-gradient-to-r group-hover:bg-card': sticky === 'right'
  });
}
function TableCaption(t0) {
  const $ = _c(8);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn("text-muted-foreground mt-4 text-sm", className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== t1) {
    t2 = <div data-slot="table-caption" className={t1} {...props} />;
    $[5] = props;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
}
const SORT_CLASS_MAP = {
  asc: 'scale-y-100',
  desc: '-scale-y-100 -translate-y-[2px]'
};
export function TableSortIcon(t0) {
  const $ = _c(11);
  const {
    isSorted,
    nextSortingOrder,
    className,
    isSortedClassName
  } = t0;
  let t1;
  if ($[0] !== isSorted || $[1] !== nextSortingOrder) {
    t1 = function renderSortClass() {
      if (isSorted) {
        return SORT_CLASS_MAP[isSorted];
      }
      return SORT_CLASS_MAP[nextSortingOrder as SortDirection];
    };
    $[0] = isSorted;
    $[1] = nextSortingOrder;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const renderSortClass = t1;
  const t2 = isSorted && "text-secondary-foreground group-hover/head:text-foreground opacity-100";
  let t3;
  if ($[3] !== className || $[4] !== isSorted || $[5] !== isSortedClassName || $[6] !== renderSortClass || $[7] !== t2) {
    t3 = cn("text-secondary-foreground inline-block origin-center text-xs opacity-0 transition-all group-hover/head:opacity-100", t2, renderSortClass(), className, isSorted && isSortedClassName);
    $[3] = className;
    $[4] = isSorted;
    $[5] = isSortedClassName;
    $[6] = renderSortClass;
    $[7] = t2;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  let t4;
  if ($[9] !== t3) {
    t4 = <span className={t3}>↑</span>;
    $[9] = t3;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  return t4;
}
type VirtualItem = {
  key?: React.Key;
  index: number;
  start: number;
  size: number;
};
function VirtualTableBody({
  height,
  className,
  style,
  children,
  parentRef,
  viewportHeight,
  ...props
}: React.ComponentProps<'div'> & {
  height: number;
  parentRef?: React.Ref<HTMLDivElement>;
  viewportHeight?: string;
}) {
  const isPercentageHeight = typeof viewportHeight === 'string' && viewportHeight.trim().endsWith('%');
  const localRef = React.useRef<HTMLDivElement | null>(null);

  // Set the same scrollbar width var used by the header for virtualized tables
  React.useLayoutEffect(() => {
    function updateScrollbarVar() {
      const current = localRef.current;
      if (!current) return;
      const scrollbarWidth = current.offsetWidth - current.clientWidth;
      current.closest<HTMLElement>('[data-slot="table"]')?.style.setProperty('--scrollbar-width', `${Math.max(0, scrollbarWidth)}px`);
    }
    updateScrollbarVar();
    const current_0 = localRef.current;
    if (!current_0) return;
    const resizeObserver = new ResizeObserver(() => updateScrollbarVar());
    resizeObserver.observe(current_0);
    return () => resizeObserver.disconnect();
  }, []);

  // Merge provided ref with our local ref
  const setRefs = (el: HTMLDivElement | null) => {
    localRef.current = el;
    if (!parentRef) return;
    if (typeof parentRef === 'function') {
      parentRef(el);
    } else if (typeof parentRef === 'object') {
      // oxlint-disable-next-line typescript/no-explicit-any
      ;
      (parentRef as any).current = el;
    }
  };
  return <div data-slot="table-virtual-body" role="rowgroup" ref={setRefs}
  // `overflow-x-hidden`: `overflow-y-auto` alone computes overflow-x to `auto`,
  // making this a nested horizontal scroll container that latches wheel gestures
  // away from the table container's real x-scroller — columns then never move.
  className={cn('relative min-h-0 overflow-x-hidden overflow-y-auto', className, {
    'h-full flex-1': isPercentageHeight
  })} style={{
    ...(style as React.CSSProperties),
    height: viewportHeight ?? '400px'
  }} {...props}>
      <div style={{
      height: `${height}px`,
      position: 'relative'
    }}>{children}</div>
    </div>;
}
function VirtualTableRow(t0) {
  const $ = _c(48);
  let children;
  let className;
  let expandedContent;
  let isLoading;
  let measureRef;
  let props;
  let style;
  let t1;
  let virtualItem;
  if ($[0] !== t0) {
    ({
      virtualItem,
      className,
      style,
      isLoading,
      measureRef,
      expandedContent,
      enableHover: t1,
      children,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = expandedContent;
    $[4] = isLoading;
    $[5] = measureRef;
    $[6] = props;
    $[7] = style;
    $[8] = t1;
    $[9] = virtualItem;
  } else {
    children = $[1];
    className = $[2];
    expandedContent = $[3];
    isLoading = $[4];
    measureRef = $[5];
    props = $[6];
    style = $[7];
    t1 = $[8];
    virtualItem = $[9];
  }
  const enableHover = t1 === undefined ? true : t1;
  const t2 = enableHover && "hover:bg-muted/50";
  let t3;
  if ($[10] !== className || $[11] !== t2) {
    t3 = cn("data-[state=selected]:bg-muted grid", t2, className);
    $[10] = className;
    $[11] = t2;
    $[12] = t3;
  } else {
    t3 = $[12];
  }
  const rowClassName = t3;
  let t4;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      gridTemplateColumns: "var(--table-columns, 1fr)",
      gridAutoColumns: "1fr",
      gridAutoFlow: "column"
    };
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  const gridStyle = t4;
  const t5 = `translateY(${virtualItem?.start ?? 0}px)`;
  let t6;
  if ($[14] !== t5) {
    t6 = {
      position: "absolute",
      top: 0,
      left: 0,
      width: "100%",
      transform: t5
    };
    $[14] = t5;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  const positionStyle = t6;
  if (isLoading) {
    let t7;
    if ($[16] !== className) {
      t7 = cn("w-full", className);
      $[16] = className;
      $[17] = t7;
    } else {
      t7 = $[17];
    }
    const t8 = `${virtualItem?.size ?? 48}px`;
    let t9;
    if ($[18] !== positionStyle || $[19] !== style || $[20] !== t8) {
      t9 = {
        ...positionStyle,
        height: t8,
        ...gridStyle,
        ...style
      };
      $[18] = positionStyle;
      $[19] = style;
      $[20] = t8;
      $[21] = t9;
    } else {
      t9 = $[21];
    }
    let t10;
    if ($[22] !== props || $[23] !== t7 || $[24] !== t9) {
      t10 = <Skeleton role="row" data-slot="table-virtual-row" className={t7} style={t9} {...props} />;
      $[22] = props;
      $[23] = t7;
      $[24] = t9;
      $[25] = t10;
    } else {
      t10 = $[25];
    }
    return t10;
  }
  if (measureRef) {
    const t7 = virtualItem?.index;
    let t8;
    if ($[26] !== style) {
      t8 = {
        ...gridStyle,
        ...style
      };
      $[26] = style;
      $[27] = t8;
    } else {
      t8 = $[27];
    }
    let t9;
    if ($[28] !== children || $[29] !== props || $[30] !== rowClassName || $[31] !== t8) {
      t9 = <div data-slot="table-virtual-row" role="row" className={rowClassName} style={t8} {...props}>{children}</div>;
      $[28] = children;
      $[29] = props;
      $[30] = rowClassName;
      $[31] = t8;
      $[32] = t9;
    } else {
      t9 = $[32];
    }
    let t10;
    if ($[33] !== expandedContent || $[34] !== measureRef || $[35] !== positionStyle || $[36] !== t7 || $[37] !== t9) {
      t10 = <div ref={measureRef} data-index={t7} style={positionStyle}>{t9}{expandedContent}</div>;
      $[33] = expandedContent;
      $[34] = measureRef;
      $[35] = positionStyle;
      $[36] = t7;
      $[37] = t9;
      $[38] = t10;
    } else {
      t10 = $[38];
    }
    return t10;
  }
  const t7 = `${virtualItem?.size ?? 48}px`;
  let t8;
  if ($[39] !== positionStyle || $[40] !== style || $[41] !== t7) {
    t8 = {
      ...positionStyle,
      height: t7,
      ...gridStyle,
      ...style
    };
    $[39] = positionStyle;
    $[40] = style;
    $[41] = t7;
    $[42] = t8;
  } else {
    t8 = $[42];
  }
  let t9;
  if ($[43] !== children || $[44] !== props || $[45] !== rowClassName || $[46] !== t8) {
    t9 = <div data-slot="table-virtual-row" role="row" className={rowClassName} style={t8} {...props}>{children}</div>;
    $[43] = children;
    $[44] = props;
    $[45] = rowClassName;
    $[46] = t8;
    $[47] = t9;
  } else {
    t9 = $[47];
  }
  return t9;
}
export { Table, TableBody, TableCaption, TableCell, TableFooter, TableHead, TableHeader, TableRow, VirtualTableBody, VirtualTableRow };